/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-07
 */

import { Alert, AlertTitle, Button, Box } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

// Error types for categorization
export type ErrorType =
  | 'network'
  | 'server'
  | 'client'
  | 'ratelimit'
  | 'validation'
  | 'unknown';

// Error alert props interface
interface ErrorAlertProps {
  error: string | null;
  errorType?: ErrorType;
  onRetry?: () => void;
  onDismiss?: () => void;
  showRetry?: boolean;
  retryLabel?: string;
}

/**
 * Enhanced error alert component for displaying API errors with categorization and retry functionality.
 *
 * @example
 * return (
 *   <ErrorAlert
 *     error="Network connection failed"
 *     errorType="network"
 *     onRetry={handleRetry}
 *     showRetry={true}
 *   />
 * )
 * @param {ErrorAlertProps} props - Component props
 * @returns {React.ReactElement | null} Error alert component or null if no error
 */
const ErrorAlert: React.FC<ErrorAlertProps> = ({
  error,
  errorType = 'unknown',
  onRetry,
  onDismiss,
  showRetry = false,
  retryLabel = 'Try Again',
}) => {
  if (!error) return null;

  // Determine alert severity based on error type
  const getSeverity = (type: ErrorType) => {
    switch (type) {
      case 'network':
        return 'warning';
      case 'server':
        return 'error';
      case 'client':
        return 'error';
      case 'ratelimit':
        return 'warning';
      case 'validation':
        return 'error';
      default:
        return 'error';
    }
  };

  // Get user-friendly title based on error type
  const getTitle = (type: ErrorType) => {
    switch (type) {
      case 'network':
        return 'Connection Problem';
      case 'server':
        return 'Server Error';
      case 'client':
        return 'Request Error';
      case 'ratelimit':
        return 'Too Many Requests';
      case 'validation':
        return 'Validation Error';
      default:
        return 'Error';
    }
  };

  const severity = getSeverity(errorType);
  const title = getTitle(errorType);

  return (
    <Alert
      severity={severity}
      onClose={onDismiss}
      sx={{
        mb: 2,
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
    >
      <AlertTitle sx={{ fontWeight: 600 }}>{title}</AlertTitle>
      {error}

      {showRetry && onRetry && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            {retryLabel}
          </Button>
        </Box>
      )}
    </Alert>
  );
};

export default ErrorAlert;
